import { useCallback, useContext, useMemo } from 'react';
import useEvents from '../../../../hooks/useEvents';
import { trackGTMEvent } from '../../../../shared/analytics/gtm';
import { EVENT_TYPES } from '../../../../shared/constants/events';
import { formatWorkflowId } from '../../../../shared/utils/linking';
import { resolveBindError } from '../bindErrors';
import { mapLinkingError } from '../mapLinkingError';
import { LinkingOriginContext } from './linkingOriginContext';
import type { BindErrorCode } from '../bindErrors';
import type { RfcPrefillTracking } from '../../../../shared/fiscal/lib/rfcPrefill';
import type { LinkingOrigin } from '../../linkingOrigin';
import type { LinkingMethod, LinkingSubmitResult } from '../../useLinkingWorkflow';
import type { WaitingVariant } from '../waiting-experience/waitingVariants';

/**
 * Interacciones puntuales del flujo, emitidas como `action` dentro del mismo
 * evento `linking`. La unión es cerrada a propósito: un typo en un `action`
 * nuevo rompe el build en vez de fabricar un valor huérfano en el funnel.
 */
/** Qué abrió el modal de ayuda de credenciales. Viaja en `opened_from` de
 * `help_modal_option_clicked` y `help_modal_closed` para poder leer el embudo
 * completo de cada puerta: la abrió → tocó algo → cómo se fue. */
export type HelpModalTrigger =
	| 'description_help'
	| 'efirma_recovery_link'
	| 'password_help'
	| 'password_error';

/** Qué tocó dentro del modal de ayuda. */
export type HelpModalOption = 'video' | 'article' | 'sat_portal' | 'switch_method';

/** Desde dónde se abrió el drawer «Generar mi RFC»: el «?» pegado al input de
 * RFC o el «?» azul del subtítulo (en RFC y contraseña ese «?» abre el drawer,
 * no el modal de ayuda). Va como `entry_point` en rfc_drawer_opened. */
export type RfcHelpEntryPoint = 'field_help' | 'description_help';

/** Cómo se cerró el modal de ayuda: `button` es el Cerrar; `dismiss` la X, el
 * fondo o el swipe del drawer; `option` es que se fue por una opción que cambia
 * de método. */
export type HelpModalCloseReason = 'button' | 'dismiss' | 'option';

export type LinkingAction =
	| 'select_method'
	| 'click_asat_faq'
	| 'click_subscription_faq'
	| 'password_help_opened'
	| 'password_error_clicked'
	| 'efirma_recovery_help_opened'
	| 'description_help_opened'
	| 'description_tooltip_shown'
	| 'help_modal_option_clicked'
	| 'help_modal_closed'
	| 'rfc_prefilled'
	| 'rfc_prefill_missed'
	| 'rfc_drawer_opened'
	| 'rfc_gate_has_rfc'
	| 'rfc_gate_never_filed'
	| 'rfc_generate_submitted'
	| 'rfc_generate_succeeded'
	| 'rfc_generate_failed'
	| 'rfc_result_used'
	| 'rfc_result_edit_requested'
	| 'asat_promo_viewed'
	| 'asat_activate_clicked'
	| 'asat_added_to_cart'
	| 'asat_add_to_cart_failed'
	| 'efirma_file_attached'
	| 'efirma_file_removed'
	| 'support_rescue_shown'
	| 'support_rescue_opened'
	| 'support_rescue_dismissed';

// Funnel único de tracking del flujo de vinculación (GTM + EVENT_TYPES.LINKING).
// Funciones estables (useCallback) para que los efectos del orquestador no se
// re-disparen por identidad.
//
// `originOverride`: la superficie de quien emite FUERA de un LinkingFlow (el
// host de onboarding v3, cuyas pantallas de vinculación son propias).
export const useLinkingTracking = (originOverride?: LinkingOrigin) => {
	const { trackEvent } = useEvents();
	// La superficie desde la que se abrió el flujo, heredada del montaje. Va en
	// el payload BASE para que la hereden los 22 `action` y los 9 `status` del
	// evento `linking` sin que cada emisor tenga que acordarse de mandarla.
	const mountOrigin = useContext(LinkingOriginContext);
	const origin = originOverride ?? mountOrigin;
	const basePayload = useMemo(() => ({ origin, version: '2' }), [origin]);

	const trackPageView = useCallback(
		(method: LinkingMethod, workflowId: string | null) => {
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					step: 1,
					type: method,
					workflowId: formatWorkflowId(workflowId ?? ''),
					status: 'on_load_page',
					origin_page: window.location.pathname,
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	const trackSubmitOutcome = useCallback(
		(
			method: LinkingMethod,
			result: LinkingSubmitResult,
			rfcPrefillTracking?: RfcPrefillTracking | null,
		) => {
			if (result.ok) {
				trackGTMEvent(EVENT_TYPES.GTM_LINKING_STEP_2_SUBMITTED, {
					type: method,
					workflow_id: formatWorkflowId(result.workflowId),
				});
				trackEvent({
					eventName: EVENT_TYPES.LINKING,
					eventPayload: {
						step: 3,
						type: method,
						workflowId: formatWorkflowId(result.workflowId),
						status: 'started',
						...rfcPrefillTracking,
						...basePayload,
					},
				});
				return;
			}
			trackGTMEvent(EVENT_TYPES.GTM_LINKING_STEP_2_FAILED, {
				type: method,
				error_message: result.errorMessage,
			});
			const resolvedAction = resolveBindError(
				undefined,
				mapLinkingError(result.errorMessage),
			).action;
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					status: 'failed',
					step: 2,
					type: method,
					failed_message: result.errorMessage,
					error_code: null,
					resolved_action: resolvedAction,
					...rfcPrefillTracking,
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	const trackCompleted = useCallback(
		(
			method: LinkingMethod,
			workflowId: string | null,
			rfcPrefillTracking?: RfcPrefillTracking | null,
		) => {
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					status: 'completed',
					step: 3,
					type: method,
					workflowId: formatWorkflowId(workflowId ?? ''),
					...rfcPrefillTracking,
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	const trackFailed = useCallback(
		(
			method: LinkingMethod,
			failedMessage: string,
			errorCode: BindErrorCode | null = null,
			rfcPrefillTracking?: RfcPrefillTracking | null,
		) => {
			const resolvedAction = resolveBindError(
				errorCode ?? undefined,
				mapLinkingError(failedMessage),
			).action;
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					status: 'failed',
					step: 3,
					type: method,
					failed_message: failedMessage,
					error_code: errorCode,
					resolved_action: resolvedAction,
					...rfcPrefillTracking,
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	const trackWaitingVariant = useCallback(
		(
			method: LinkingMethod,
			workflowId: string | null,
			variant: WaitingVariant,
		) => {
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					step: 3,
					type: method,
					workflowId: formatWorkflowId(workflowId ?? ''),
					status: 'waiting_variant_shown',
					waiting_variant: variant,
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	const trackWaitingVariantInteracted = useCallback(
		(
			method: LinkingMethod,
			workflowId: string | null,
			variant: WaitingVariant,
			action: string,
			extra?: Record<string, unknown>,
		) => {
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					step: 3,
					type: method,
					workflowId: formatWorkflowId(workflowId ?? ''),
					status: 'waiting_variant_interacted',
					waiting_variant: variant,
					action,
					...(extra ?? {}),
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	const trackCancelled = useCallback(
		(method: LinkingMethod, workflowId: string | null) => {
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					status: 'cancelled',
					step: 3,
					type: method,
					workflowId: formatWorkflowId(workflowId ?? ''),
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	// Elección de método en el step 1: hasta ahora solo existía en GTM, así que
	// el funnel de Amplitude/DB no podía cortar la conversión por método.
	const trackMethodSelected = useCallback(
		(method: LinkingMethod) => {
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					step: 1,
					type: method,
					action: 'select_method',
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	// Toda interacción de la pantalla de credenciales (step 2): ayudas, drawer de
	// RFC, oferta de ASAT y archivos de e.firma. `type` siempre lleva el método
	/**
	 * Etapa 3 del embudo: el usuario vio el formulario de credenciales. Vive en
	 * el hook y no en el componente para que herede `origin` como todos los
	 * demás: emitiéndolo suelto se quedaba sin superficie y el corte por pantalla
	 * se partía justo en la etapa que separa "eligió método" de "envió".
	 */
	const trackCredentialsView = useCallback(
		(method: LinkingMethod, errorMessage?: string) => {
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					step: 2,
					type: method,
					errorMessage: errorMessage || undefined,
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	// para poder cortar el funnel por contraseña vs e.firma.
	const trackAction = useCallback(
		(
			method: LinkingMethod,
			action: LinkingAction,
			extra?: Record<string, unknown>,
		) => {
			trackEvent({
				eventName: EVENT_TYPES.LINKING,
				eventPayload: {
					step: 2,
					type: method,
					action,
					...(extra ?? {}),
					...basePayload,
				},
			});
		},
		[trackEvent, basePayload],
	);

	return {
		trackPageView,
		trackSubmitOutcome,
		trackCompleted,
		trackFailed,
		trackCancelled,
		trackWaitingVariant,
		trackWaitingVariantInteracted,
		trackMethodSelected,
		trackAction,
		trackCredentialsView,
	};
};
